import React, { useState, useMemo } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjectWorkOrders, useWorkOrderLines, useProjectContracts, useContractSovLines } from "@/hooks/useProjectContracts";
import { useVendors, getComplianceStatus } from "@/hooks/useVendors";
import { useUnitSchedule, useUnitScopeItems } from "@/hooks/useUnitSchedule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Plus, FileText, ArrowLeft, AlertTriangle, ChevronDown, ChevronRight, Pencil, Ban, Lock, MoreVertical, Zap, Receipt } from "lucide-react";
import { WOVoidModal, WOCloseModal } from "./WOVoidCloseModals";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { generateWONumber } from "@/lib/naming";
import { GenerateWODialog } from "./GenerateWODialog";
import { WOExpandedContent } from "./WOExpandedContent";
import { WOAssignmentGrid, cellKey, parseKey, type CellKey } from "./WOAssignmentGrid";
import { WOCreateBillDialog } from "./WOCreateBillDialog";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtRate = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);
const pct = (n: number) => `${n.toFixed(1)}%`;

function VendorAvatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  if (photoUrl) return <img src={photoUrl} alt={name} className="w-7 h-7 rounded-full object-cover shrink-0" />;
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ backgroundColor: `hsl(${hue}, 55%, 45%)` }}>
      {initials}
    </div>
  );
}

function ComplianceDot({ level }: { level: "green" | "yellow" | "red" }) {
  const colors = { green: "bg-green-500", yellow: "bg-yellow-500", red: "bg-destructive" };
  return <span className={cn("inline-block w-2.5 h-2.5 rounded-full shrink-0", colors[level])} />;
}

interface Props {
  projectId: string;
  project?: any;
  onMutate: () => void;
}

export const ProjectWOTab = ({ projectId, project, onMutate }: Props) => {
  const isMobile = useIsMobile();
  const { can, isAdmin } = usePermissions();
  const woPagination = usePagination();
  const { data: workOrders, refetch: refetchWOs } = useProjectWorkOrders(projectId, woPagination);
  const { data: woLines, refetch: refetchWoLines } = useWorkOrderLines(projectId);
  const { data: contracts } = useProjectContracts(projectId);
  const contract = contracts?.[0];
  const { data: sovLines } = useContractSovLines(contract?.id);
  const { data: vendors } = useVendors();
  const { data: units } = useUnitSchedule(projectId);
  const { data: scopeItems } = useUnitScopeItems(projectId);

  const hasUnits = (units ?? []).length > 0;
  const billingType = project?.billing_type ?? "unit_based";

  const [assignOpen, setAssignOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedCells, setSelectedCells] = useState<Set<CellKey>>(new Set());
  // Legacy SOV-only state
  const [selectedSovLineIds, setSelectedSovLineIds] = useState<Set<string>>(new Set());
  const [rates, setRates] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [generateWO, setGenerateWO] = useState<any>(null);
  const [expandedWOs, setExpandedWOs] = useState<Set<string>>(new Set());
  const [bulkRate, setBulkRate] = useState("");
  const [assignQty, setAssignQty] = useState<Record<string, string>>({});
  const [rateOverrideReasons, setRateOverrideReasons] = useState<Record<string, string>>({});
  // Edit WO state
  const [editWO, setEditWO] = useState<any>(null);
  const [editWODesc, setEditWODesc] = useState("");
  const [editWOVendor, setEditWOVendor] = useState("");
  const [editWOBusy, setEditWOBusy] = useState(false);
  // WO type for new WOs
  const [woType, setWoType] = useState<string>("blanket");
  const [woPhase, setWoPhase] = useState("");
  // Existing WO detection
  const [existingWOChoice, setExistingWOChoice] = useState<"add" | "new" | null>(null);
  // Add units to existing WO
  const [addUnitsWO, setAddUnitsWO] = useState<any>(null);
  // Void/Close modals
  const [voidWO, setVoidWO] = useState<any>(null);
  const [closeWO, setCloseWO] = useState<any>(null);
  const [woVoidableCache, setWoVoidableCache] = useState<Record<string, boolean>>({});
  // Create Bill dialog
  const [billWO, setBillWO] = useState<any>(null);

  const canEdit = isAdmin || can("edit_work_orders");

  const woCostBySovLine = useMemo(() => {
    const map: Record<string, number> = {};
    (woLines ?? []).forEach((wol: any) => {
      const wo = wol.work_orders;
      if (wo && wo.status !== "rejected" && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed") {
        if (wol.status !== "voided" && wol.status !== "released") {
          map[wol.sov_line_id] = (map[wol.sov_line_id] || 0) + Number(wol.quantity) * Number(wol.contract_rate);
        }
      }
    });
    return map;
  }, [woLines]);

  const refreshAll = () => { refetchWOs(); refetchWoLines(); onMutate(); };

  // --- Edit WO handler ---
  const handleEditWO = (wo: any) => {
    setEditWO(wo);
    setEditWODesc(wo.description ?? "");
    setEditWOVendor(wo.contractor_id ?? "");
  };

  const handleSaveEditWO = async () => {
    if (!editWO) return;
    setEditWOBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      const oldVendor = vendors?.find(v => v.id === editWO.contractor_id);
      const newVendor = vendors?.find(v => v.id === editWOVendor);

      const { error } = await supabase.from("work_orders").update({
        description: editWODesc,
        contractor_id: editWOVendor || null,
      } as any).eq("id", editWO.id);
      if (error) throw error;

      // Audit log
      const changes: string[] = [];
      if (editWODesc !== (editWO.description ?? "")) changes.push(`description: "${editWO.description}" → "${editWODesc}"`);
      if (editWOVendor !== (editWO.contractor_id ?? "")) changes.push(`contractor: "${oldVendor?.name ?? "none"}" → "${newVendor?.name ?? "none"}"`);

      if (changes.length > 0) {
        await supabase.from("ops_audit_log").insert({
          table_name: "work_orders",
          record_id: editWO.id,
          action: "wo_edited",
          old_status: editWO.status,
          new_status: editWO.status,
          changed_by: (await supabase.auth.getUser()).data.user?.id,
          metadata: { changes },
        } as any);
      }

      toast.success("Work order updated");
      setEditWO(null);
      refreshAll();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setEditWOBusy(false);
    }
  };

  // --- Guard check: can this WO be voided? ---
  const checkVoidable = async (woId: string): Promise<boolean> => {
    if (woVoidableCache[woId] !== undefined) return woVoidableCache[woId];
    try {
      const woLineIds = (woLines ?? []).filter((wol: any) => wol.work_order_id === woId).map((wol: any) => wol.id);
      if (woLineIds.length === 0) {
        setWoVoidableCache(prev => ({ ...prev, [woId]: true }));
        return true;
      }
      const { count } = await supabase
        .from("wo_draw_lines")
        .select("*", { count: "exact", head: true })
        .in("work_order_line_id", woLineIds);
      const voidable = (count ?? 0) === 0;
      setWoVoidableCache(prev => ({ ...prev, [woId]: voidable }));
      return voidable;
    } catch {
      return false;
    }
  };

  const handleVoidOrClose = async (wo: any) => {
    const canVoid = await checkVoidable(wo.id);
    if (canVoid) {
      setVoidWO(wo);
    } else {
      setCloseWO(wo);
    }
  };

  const handleNewWO = () => {
    if (!contract || !(sovLines ?? []).length) {
      toast.error("Create a contract with SOV lines first");
      return;
    }
    setStep(1);
    setSelectedVendorId("");
    setSelectedCells(new Set());
    setSelectedSovLineIds(new Set());
    setRates({});
    setAssignQty({});
    setWoType("blanket");
    setWoPhase("");
    setExistingWOChoice(null);
    setAddUnitsWO(null);
    setAssignOpen(true);
  };

  // Detect existing open blanket WO for selected vendor
  const existingOpenWO = useMemo(() => {
    if (!selectedVendorId) return null;
    return (workOrders ?? []).find((wo: any) =>
      wo.contractor_id === selectedVendorId &&
      wo.status !== "cancelled" && wo.status !== "rejected" &&
      (wo.wo_type === "blanket" || !wo.wo_type)
    );
  }, [selectedVendorId, workOrders]);

  // Handle "Add Units" to existing WO
  const handleAddUnitsToWO = (wo: any) => {
    if (!contract || !(sovLines ?? []).length) {
      toast.error("No contract/SOV lines");
      return;
    }
    setAddUnitsWO(wo);
    setSelectedVendorId(wo.contractor_id);
    setSelectedCells(new Set());
    setRates({});
    setAssignQty({});
    setAssignOpen(true);
    setStep(1);
    setExistingWOChoice("add");
  };

  const selectedVendor = vendors?.find(v => v.id === selectedVendorId);
  const vendorCompliance = selectedVendor ? getComplianceStatus(selectedVendor) : null;

  // For SOV-only mode (no units)
  const availableSovLines = useMemo(() => {
    return (sovLines ?? []).map((line: any) => {
      const sovValue = Number(line.scheduled_value);
      const committed = woCostBySovLine[line.id] || 0;
      const remaining = sovValue - committed;
      const fullyAssigned = remaining <= 0;
      return { ...line, committed, remaining, fullyAssigned };
    });
  }, [sovLines, woCostBySovLine]);

  const selectedLines = availableSovLines.filter(l => selectedSovLineIds.has(l.id));
  const selectableLines = availableSovLines.filter(l => !l.fullyAssigned);

  const toggleLine = (id: string) => {
    if (!selectedVendorId) { toast.error("Select a contractor first"); return; }
    const line = availableSovLines.find(l => l.id === id);
    if (line?.fullyAssigned) return;
    setSelectedSovLineIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const toggleAll = () => {
    if (!selectedVendorId) { toast.error("Select a contractor first"); return; }
    if (selectedSovLineIds.size === selectableLines.length) setSelectedSovLineIds(new Set());
    else setSelectedSovLineIds(new Set(selectableLines.map(l => l.id)));
  };

  // Grid cell toggle for unit-based
  const handleToggleCell = (key: CellKey) => {
    setSelectedCells(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectionChange = (keys: CellKey[]) => setSelectedCells(new Set(keys));

  // Build rate data from grid selection
  const gridSovLineIds = useMemo(() => {
    const ids = new Set<string>();
    selectedCells.forEach(key => { const { sovLineId } = parseKey(key); ids.add(sovLineId); });
    return Array.from(ids);
  }, [selectedCells]);

  const gridSelectedLines = useMemo(() => {
    return gridSovLineIds.map(id => availableSovLines.find(l => l.id === id)).filter(Boolean) as any[];
  }, [gridSovLineIds, availableSovLines]);

  const goToStep2 = () => {
    const linesToUse = hasUnits ? gridSelectedLines : selectedLines;
    const newRates: Record<string, string> = {};
    const newQtys: Record<string, string> = {};

    linesToUse.forEach((line: any) => {
      // Pre-fill rate from sub_unit_price (estimated sub cost), fall back to sub_lines average, then labor_unit_price
      const subRate = Number(line.sub_unit_price ?? 0);
      if (subRate > 0) {
        newRates[line.id] = String(subRate);
      } else {
        const subLines = Array.isArray(line.sub_lines) ? line.sub_lines : [];
        const laborSubs = subLines.filter((sl: any) => sl.type === "labor" && sl.cost_price > 0);
        if (laborSubs.length > 0) {
          const avg = laborSubs.reduce((s: number, sl: any) => s + Number(sl.cost_price), 0) / laborSubs.length;
          newRates[line.id] = String(Math.round(avg * 100) / 100);
        } else {
          const laborRate = Number(line.labor_unit_price ?? line.unit_price);
          newRates[line.id] = laborRate > 0 ? String(laborRate) : "";
        }
      }

      // Bug 2 fix: initialize assignQty with full available quantity (user can reduce)
      if (hasUnits) {
        let qty = 0;
        selectedCells.forEach(key => {
          const { sovLineId, unitId } = parseKey(key);
          if (sovLineId === line.id) {
            const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
            qty += si ? Number(si.quantity) : 0;
          }
        });
        newQtys[line.id] = String(qty);
      } else {
        newQtys[line.id] = String(Number(line.quantity));
      }
    });

    setRates(newRates);
    setAssignQty(newQtys);
    setStep(2);
  };

  const applyRateToAll = (rate: string) => {
    const newRates: Record<string, string> = {};
    const linesToUse = hasUnits ? gridSelectedLines : selectedLines;
    linesToUse.forEach((l: any) => { newRates[l.id] = rate; });
    setRates(newRates);
  };

  const getMargin = (sovRate: number, myRate: number) => {
    if (sovRate <= 0) return null;
    if (myRate <= 0) return 100;
    return ((sovRate - myRate) / sovRate) * 100;
  };

  const rateLines = hasUnits ? gridSelectedLines : selectedLines;

  const allRatesEntered = rateLines.every((l: any) => {
    const val = rates[l.id];
    return val !== undefined && val !== "";
  });

  const zeroRateLines = rateLines.filter((l: any) => Number(rates[l.id]) === 0);

  const woTotalAll = rateLines.reduce((s: number, l: any) => {
    const r = Number(rates[l.id]) || 0;
    if (hasUnits) {
      // Unit-based: qty controlled by cell selection in Step 1
      let qty = 0;
      selectedCells.forEach(key => {
        const { sovLineId, unitId } = parseKey(key);
        if (sovLineId === l.id) {
          const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
          qty += si ? Number(si.quantity) : 0;
        }
      });
      return s + qty * r;
    }
    // SOV-only: use assignQty (partial assignment) if set, else full SOV qty
    const qty = assignQty[l.id] !== undefined && assignQty[l.id] !== ""
      ? Number(assignQty[l.id])
      : Number(l.quantity);
    return s + qty * r;
  }, 0);

  const sovBudgetAll = rateLines.reduce((s: number, l: any) => {
    const laborRate = Number(l.labor_unit_price ?? l.unit_price);
    if (hasUnits) {
      let qty = 0;
      selectedCells.forEach(key => {
        const { sovLineId, unitId } = parseKey(key);
        if (sovLineId === l.id) {
          const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
          qty += si ? Number(si.quantity) : 0;
        }
      });
      return s + qty * laborRate;
    }
    return s + Number(l.quantity) * laborRate;
  }, 0);

  const overallMargin = sovBudgetAll > 0 ? ((sovBudgetAll - woTotalAll) / sovBudgetAll) * 100 : 0;

  const parseRateError = (msg: string): string | null => {
    if (msg.includes("RATE_EXCEEDS_SELL_RATE")) return "Rate must be less than the sell rate — zero-margin subcontractor pricing is not allowed.";
    if (msg.includes("RATE_OVERRIDE_NOT_APPROVED")) return "Rate exceeds estimated sub cost and requires approval from an authorized user.";
    return null;
  };

  const handleCreateWOs = async () => {
    if (!allRatesEntered) return toast.error("Enter a rate for every line (0 is allowed)");
    setBusy(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) { setBusy(false); return toast.error("Company not found"); }
    const userId = (await supabase.auth.getUser()).data.user?.id;

    for (const line of rateLines) {
      const rate = Number(rates[line.id]);
      const laborRate = Number(line.labor_unit_price ?? line.unit_price);
      const subRate = Number(line.sub_unit_price ?? 0);

      // Hard block: rate >= sell rate (zero margin not allowed)
      if (rate > 0 && rate >= laborRate) {
        setBusy(false);
        return toast.error(`Rate for ${line.description} must be less than sell rate (${fmtRate(laborRate)}) — zero-margin pricing is not allowed`);
      }

      // Override gate: rate > estimated sub rate
      if (subRate > 0 && rate > subRate) {
        if (!rateOverrideReasons[line.id]?.trim()) {
          setBusy(false);
          return toast.error(`Rate for ${line.description} exceeds estimated sub rate (${fmtRate(subRate)}). Enter a variance reason.`);
        }
        if (!can("approve_rate_overrides") && !isAdmin) {
          setBusy(false);
          return toast.error("Rate override requires approval from an authorized user (approve_rate_overrides permission).");
        }
      }
    }

    // If adding to existing WO, just add lines
    if (addUnitsWO) {
      let woLineRows: any[] = [];
      selectedCells.forEach(key => {
        const { unitId, sovLineId } = parseKey(key);
        const sl = (sovLines ?? []).find((s: any) => s.id === sovLineId);
        const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
        if (!sl || !si) return;
        const rate = Number(rates[sovLineId]) || 0;
        const qty = Number(si.quantity);
        const laborRate = Number(sl.labor_unit_price ?? sl.unit_price);
        const subRate = Number(sl.sub_unit_price ?? 0);
        const marginPct = laborRate > 0 ? ((laborRate - rate) / laborRate) * 100 : 0;
        const isOverride = subRate > 0 && rate > subRate;
        woLineRows.push({
          work_order_id: addUnitsWO.id,
          sov_line_id: sovLineId,
          unit_id: unitId,
          company_id: companyId,
          description: sl.description,
          quantity: qty,
          unit: sl.unit ?? "",
          contract_rate: rate,
          line_total: qty * rate,
          sov_rate: laborRate,
          margin_pct: Math.round(marginPct * 10) / 10,
          status: "pending",
          ...(isOverride ? {
             rate_source: "manual_override",
             rate_override_reason: rateOverrideReasons[sovLineId] ?? "",
             rate_approved_by: userId,
             rate_approved_at: new Date().toISOString(),
           } : { rate_source: "estimate" }),
         });
       });

      // INSERT the lines into the database
      const { error: insertErr } = await supabase
        .from("work_order_lines")
        .insert(woLineRows as any);
      if (insertErr) {
        setBusy(false);
        toast.error(insertErr.message);
        return;
      }

      // Audit log override lines
      const overrideLines = woLineRows.filter((r: any) => r.rate_source === "manual_override");
      for (const ol of overrideLines) {
        const sl = (sovLines ?? []).find((s: any) => s.id === ol.sov_line_id);
        await supabase.from("ops_audit_log").insert({
          table_name: "work_order_lines",
          record_id: addUnitsWO.id,
          action: "wo_rate_override",
          changed_by: userId,
          metadata: {
            sov_line_id: ol.sov_line_id,
            estimated_sub_rate: Number(sl?.sub_unit_price ?? 0),
            override_rate: ol.contract_rate,
            reason: ol.rate_override_reason,
            approver: userId,
          },
        } as any);
      }
      // Update WO total
      const newTotal = Number(addUnitsWO.amount) + woTotalAll;
      await supabase.from("work_orders").update({ amount: newTotal } as any).eq("id", addUnitsWO.id);
      setBusy(false);
      toast.success(`Added ${woLineRows.length} lines to ${addUnitsWO.number} | New total: ${fmt(newTotal)}`);
      setAssignOpen(false);
      setAddUnitsWO(null);
      refreshAll();
      return;
    }

    const vendorName = selectedVendor?.name ?? "Unknown";
    const woNumber = await generateWONumber(projectId, selectedVendorId, vendorName);

    // Calculate total value
    let totalValue = woTotalAll;

    const { data: wo, error: woErr } = await supabase.from("work_orders").insert({
      project_id: projectId,
      company_id: companyId,
      contractor_id: selectedVendorId,
      number: woNumber,
      description: `${vendorName} — ${project?.name ?? "Project"}`,
      amount: totalValue,
      wo_type: woType,
      phase: woPhase || null,
    } as any).select("id, number").single();

    if (woErr) { setBusy(false); return toast.error(woErr.message); }

    let woLineRows: any[];

    if (hasUnits) {
      // Unit-based: create one WO line per unit+SOV cell
      woLineRows = [];
      selectedCells.forEach(key => {
        const { unitId, sovLineId } = parseKey(key);
        const sl = (sovLines ?? []).find((s: any) => s.id === sovLineId);
        const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
        if (!sl || !si) return;
        const rate = Number(rates[sovLineId]) || 0;
        const qty = Number(si.quantity);
        const laborRate = Number(sl.labor_unit_price ?? sl.unit_price);
        const marginPct = laborRate > 0 ? ((laborRate - rate) / laborRate) * 100 : 0;
        const subRate = Number(sl.sub_unit_price ?? 0);
        const isOverride = subRate > 0 && rate > subRate;

        woLineRows.push({
          work_order_id: wo.id,
          sov_line_id: sovLineId,
          unit_id: unitId,
          company_id: companyId,
          description: sl.description,
          quantity: qty,
          unit: sl.unit ?? "",
          contract_rate: rate,
          line_total: qty * rate,
          sov_rate: laborRate,
          margin_pct: Math.round(marginPct * 10) / 10,
          status: "pending",
          ...(isOverride ? {
             rate_source: "manual_override",
             rate_override_reason: rateOverrideReasons[sovLineId] ?? "",
             rate_approved_by: userId,
             rate_approved_at: new Date().toISOString(),
           } : { rate_source: "estimate" }),
        });
      });
    } else {
      // SOV-only: one WO line per SOV line
      woLineRows = selectedLines.map((line: any) => {
        const rate = Number(rates[line.id]) || 0;
        // Use partial qty if user changed it, else full SOV line quantity
        const qty = assignQty[line.id] !== undefined && assignQty[line.id] !== ""
          ? Number(assignQty[line.id])
          : Number(line.quantity);
        const laborRate = Number(line.labor_unit_price ?? line.unit_price);
        const subRate = Number(line.sub_unit_price ?? 0);
        const marginPct = laborRate > 0 ? ((laborRate - rate) / laborRate) * 100 : 0;
        const isOverride = subRate > 0 && rate > subRate;
        return {
          work_order_id: wo.id,
          sov_line_id: line.id,
          company_id: companyId,
          description: line.description,
          quantity: qty,
          unit: line.unit,
          contract_rate: rate,
          line_total: qty * rate,
          sov_rate: laborRate,
          margin_pct: Math.round(marginPct * 10) / 10,
          status: "pending",
          ...(isOverride ? {
             rate_source: "manual_override",
             rate_override_reason: rateOverrideReasons[line.id] ?? "",
             rate_approved_by: userId,
             rate_approved_at: new Date().toISOString(),
           } : { rate_source: "estimate" }),
        };
      });
    }

    const { error: wolErr } = await supabase.from("work_order_lines").insert(woLineRows as any);
    if (wolErr) {
      await supabase.from("work_orders").delete().eq("id", wo.id);
      setBusy(false);
      const friendly = parseRateError(wolErr.message);
      return toast.error(friendly ?? wolErr.message);
    }

    // Audit log override lines
    const overrideLines = woLineRows.filter((r: any) => r.rate_source === "manual_override");
    for (const ol of overrideLines) {
      const sl = (sovLines ?? []).find((s: any) => s.id === ol.sov_line_id);
      await supabase.from("ops_audit_log").insert({
        table_name: "work_order_lines",
        record_id: wo.id,
        action: "wo_rate_override",
        changed_by: userId,
        metadata: {
          sov_line_id: ol.sov_line_id,
          estimated_sub_rate: Number(sl?.sub_unit_price ?? 0),
          override_rate: ol.contract_rate,
          reason: ol.rate_override_reason,
          approver: userId,
        },
      } as any);
    }

    setBusy(false);
    toast.success(`Work Order ${wo.number} created — ${woLineRows.length} line items | Total: ${fmt(totalValue)}`);
    setAssignOpen(false);
    refreshAll();
  };

  const toggleWOExpand = (woId: string) => {
    setExpandedWOs(prev => { const next = new Set(prev); if (next.has(woId)) next.delete(woId); else next.add(woId); return next; });
  };

  if (!contract) {
    return <div className="text-center py-12"><p className="text-muted-foreground">No work orders yet — create a contract first to assign work orders.</p></div>;
  }

  const stepOneHasSelection = hasUnits ? selectedCells.size > 0 : selectedSovLineIds.size > 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {can("create_work_orders") && <Button size="sm" onClick={handleNewWO}><Plus className="mr-1 h-4 w-4" />New Work Order</Button>}
      </div>

      {(() => {
        const allWOs = workOrders ?? [];
        const activeWOs = allWOs.filter((wo: any) => wo.status !== "voided" && wo.status !== "cancelled");
        const inactiveWOs = allWOs.filter((wo: any) => wo.status === "voided" || wo.status === "cancelled");

        if (!allWOs.length) {
          return (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-2">No work orders yet</p>
              <Button variant="outline" size="sm" onClick={handleNewWO}>Create your first Work Order</Button>
            </div>
          );
        }

        const renderWOCard = (wo: any, readOnly = false) => {
          const linesForWo = (woLines ?? []).filter((wol: any) => wol.work_order_id === wo.id);
          const activeLines = linesForWo.filter((wol: any) => wol.status !== "voided" && wol.status !== "released");
          const openLines = activeLines.filter((wol: any) => wol.status !== "billed");
          const billedLines = activeLines.filter((wol: any) => wol.status === "billed");
          const openTotal = openLines.reduce((s: number, wol: any) => s + Number(wol.quantity) * Number(wol.contract_rate), 0);
          const billedTotal = billedLines.reduce((s: number, wol: any) => s + Number(wol.quantity) * Number(wol.contract_rate), 0);
          const retainage = linesForWo.reduce((s: number, wol: any) => s + Number(wol.retainage_held), 0);
          const isExpanded = expandedWOs.has(wo.id);

          return (
            <Card key={wo.id} className={cn((wo.status === "voided" || wo.status === "cancelled") && "opacity-50 border-destructive/30", wo.status === "closed" && "opacity-70")}>
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => toggleWOExpand(wo.id)}>
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold">{wo.number}</span>
                      <StatusBadge status={wo.status} />
                      {wo.status === "voided" && <Badge variant="destructive" className="text-[10px]">Voided</Badge>}
                      {wo.status === "closed" && <Badge variant="secondary" className="text-[10px]">Closed</Badge>}
                      {(wo.wo_type === "blanket" || !wo.wo_type) && <Badge variant="outline" className="text-[10px]">Blanket</Badge>}
                      {wo.wo_type === "phase" && <Badge variant="outline" className="text-[10px]">Phase</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">{wo.vendors?.name ?? wo.description ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {(() => {
                        const billedCount = activeLines.filter((wol: any) => wol.status === "billed").length;
                        const openCount = activeLines.length - billedCount;
                        const parts: string[] = [];
                        if (billedCount > 0) {
                          parts.push(`${openCount} open / ${billedCount} billed`);
                        } else {
                          parts.push(`${activeLines.length} line item${activeLines.length !== 1 ? "s" : ""}`);
                        }
                        if (activeLines.length < linesForWo.length) parts.push(`${linesForWo.length - activeLines.length} voided`);
                        return parts.join(" · ");
                      })()}{wo.phase ? ` · ${wo.phase}` : ""}
                      {wo.lines_released > 0 && <span> · {wo.lines_released} released</span>}
                      {wo.lines_voided > 0 && <span> · {wo.lines_voided} voided</span>}
                      {wo.void_reason && <span className="text-destructive"> · {wo.void_reason}</span>}
                      {wo.close_reason && <span> · {wo.close_reason}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="font-semibold">{fmt(openTotal)}</div>
                    <div className="text-xs text-muted-foreground">Billed: {fmt(billedTotal)} | Ret: {fmt(retainage)}</div>
                  </div>
                  {!readOnly && (
                    isMobile ? (
                      /* Mobile: single ⋮ menu with all actions */
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {hasUnits && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (wo.wo_type === "blanket" || !wo.wo_type) && (
                            <DropdownMenuItem onClick={() => handleAddUnitsToWO(wo)}>
                              <Plus className="h-3.5 w-3.5 mr-2" /> Add Units
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setGenerateWO(wo)}>
                            <FileText className="h-3.5 w-3.5 mr-2" /> WO Document
                          </DropdownMenuItem>
                          {canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (
                            <>
                              <DropdownMenuItem onClick={() => handleEditWO(wo)}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit Work Order
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setBillWO(wo)}>
                                <Receipt className="h-3.5 w-3.5 mr-2" /> Create Bill
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCloseWO(wo)}>
                                <Lock className="h-3.5 w-3.5 mr-2" /> Close Work Order
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive" onClick={() => handleVoidOrClose(wo)}>
                                <Ban className="h-3.5 w-3.5 mr-2" /> Void Work Order
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      /* Desktop: inline buttons + overflow menu */
                      <>
                        {hasUnits && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (wo.wo_type === "blanket" || !wo.wo_type) && (
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleAddUnitsToWO(wo); }}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add Units
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setGenerateWO(wo); }}>
                          <FileText className="h-4 w-4 mr-1" /> WO Document
                        </Button>
                        {canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditWO(wo)}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit Work Order
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setBillWO(wo)}>
                                <Receipt className="h-3.5 w-3.5 mr-2" /> Create Bill
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCloseWO(wo)}>
                                <Lock className="h-3.5 w-3.5 mr-2" /> Close Work Order
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive" onClick={() => handleVoidOrClose(wo)}>
                                <Ban className="h-3.5 w-3.5 mr-2" /> Void Work Order
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </>
                    )
                  )}
                </div>
              </div>
              {isExpanded && (
                <CardContent className="pt-0 pb-3">
                  <WOExpandedContent
                    wo={wo}
                    linesForWo={activeLines}
                    billed={billedTotal}
                    projectId={projectId}
                    project={project}
                    sovLines={sovLines ?? []}
                    onMutate={refreshAll}
                    billingType={billingType}
                  />
                </CardContent>
              )}
            </Card>
          );
        };

        return (
          <Tabs defaultValue="active" className="w-full">
            <TabsList>
              <TabsTrigger value="active">Active ({activeWOs.length})</TabsTrigger>
              <TabsTrigger value="cancelled">Cancelled / Voided ({inactiveWOs.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="active">
              <div className="space-y-3">
                {activeWOs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">All work orders are cancelled or voided.</p>
                ) : activeWOs.map((wo: any) => renderWOCard(wo))}
              </div>
            </TabsContent>
            <TabsContent value="cancelled">
              <div className="space-y-3">
                {inactiveWOs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No cancelled or voided work orders.</p>
                ) : inactiveWOs.map((wo: any) => renderWOCard(wo, true))}
              </div>
            </TabsContent>
          </Tabs>
        );
      })()}
      <PaginationControls pagination={woPagination} />

      {/* Assign WO Dialog */}
      <Dialog open={assignOpen} onOpenChange={(o) => { if (!o) setAssignOpen(false); }}>
        <DialogContent className={cn("max-h-[92vh] overflow-y-auto", hasUnits ? "w-[95vw] max-w-[95vw] min-w-[600px]" : "max-w-[720px]")} style={{ resize: "both", overflow: "auto" }}>
          {step === 1 ? (
            <>
              <DialogHeader>
                <DialogTitle>{addUnitsWO ? `Add Units to ${addUnitsWO.number}` : "New Work Order"}</DialogTitle>
                <DialogDescription>
                  {addUnitsWO ? `Adding units to existing blanket WO for ${addUnitsWO.vendors?.name}` : `Select subcontractor then ${hasUnits ? "pick unit × SOV cells" : "choose SOV lines"}`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* WO Type selector (only for new WOs) */}
                {!addUnitsWO && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Type</label>
                    <div className="flex gap-2">
                      {(["blanket", "phase", "rework", "backcharge"] as const).map(t => (
                        <Button
                          key={t}
                          variant={woType === t ? "default" : "outline"}
                          size="sm"
                          onClick={() => setWoType(t)}
                          className="capitalize"
                        >
                          {t === "blanket" ? "● Blanket" : t}
                        </Button>
                      ))}
                    </div>
                    {woType === "phase" && (
                      <Input placeholder="Phase label (e.g. Phase 1 — Flooring)" value={woPhase} onChange={e => setWoPhase(e.target.value)} />
                    )}
                  </div>
                )}

                {/* Vendor selector */}
                {!addUnitsWO && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Subcontractor</label>
                    <Select value={selectedVendorId} onValueChange={(v) => { setSelectedVendorId(v); setExistingWOChoice(null); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select contractor">
                          {selectedVendor && (
                            <span className="flex items-center gap-2">
                              <VendorAvatar name={selectedVendor.name} photoUrl={(selectedVendor as any).profile_photo_url} />
                              <span>{selectedVendor.name}</span>
                              <ComplianceDot level={vendorCompliance?.level ?? "green"} />
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {(vendors ?? []).map((v) => {
                          const cs = getComplianceStatus(v);
                          const types = v.vendor_types || [v.vendor_type || "general"];
                          const typeLabel = types[0]?.replace(/_/g, " ") ?? "general";
                          return (
                            <SelectItem key={v.id} value={v.id}>
                              <span className="flex items-center gap-2">
                                <VendorAvatar name={v.name} photoUrl={(v as any).profile_photo_url} />
                                <span>{v.name}</span>
                                <ComplianceDot level={cs.level} />
                                <Badge variant="outline" className="text-[10px] capitalize">{typeLabel}</Badge>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedVendor && vendorCompliance && vendorCompliance.level !== "green" && (
                      <div className="flex items-start gap-2 p-2 rounded-md border border-yellow-200 dark:border-yellow-800 text-sm bg-yellow-50/50 dark:bg-yellow-950/50">
                        <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                        <div>
                          <span className="font-medium">{selectedVendor.name}</span>
                          <span className="text-muted-foreground"> — {vendorCompliance.issues.map(i => i.label).join(", ")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Existing WO detection */}
                {existingOpenWO && !addUnitsWO && existingWOChoice === null && woType === "blanket" && (
                  <div className="border rounded-md p-3 space-y-2 bg-muted/30">
                    <div className="flex items-start gap-2 text-sm">
                      <Zap className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium">{selectedVendor?.name}</span> already has an open blanket WO:
                        <div className="font-mono text-xs mt-1">
                          {existingOpenWO.number} · {(woLines ?? []).filter((wol: any) => wol.work_order_id === existingOpenWO.id).length} lines assigned
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant={existingWOChoice === "add" ? "default" : "outline"} onClick={() => { setExistingWOChoice("add"); setAddUnitsWO(existingOpenWO); }}>
                        Add units to {existingOpenWO.number}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setExistingWOChoice("new")}>
                        Create new WO anyway
                      </Button>
                    </div>
                  </div>
                )}

                {/* Assignment Grid (unit-based) or SOV line picker (SOV-only) */}
                {selectedVendorId && (
                  hasUnits ? (
                    <WOAssignmentGrid
                      units={units ?? []}
                      sovLines={sovLines ?? []}
                      scopeItems={scopeItems ?? []}
                      existingWoLines={woLines ?? []}
                      contractorId={selectedVendorId}
                      selectedCells={selectedCells}
                      onToggleCell={handleToggleCell}
                      onSelectionChange={handleSelectionChange}
                      onUnassign={async (wolId: string) => {
                        // Delete the WO line to unassign
                        const { error } = await supabase
                          .from("work_order_lines")
                          .update({ status: "voided" } as any)
                          .eq("id", wolId);
                        if (error) throw error;
                        refreshAll();
                      }}
                    />
                  ) : (
                    /* SOV-only picker */
                    <div className="space-y-1">
                      <span className="text-sm font-medium">Select SOV Lines</span>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox
                                  checked={selectableLines.length > 0 && selectedSovLineIds.size === selectableLines.length}
                                  onCheckedChange={toggleAll}
                                />
                              </TableHead>
                              <TableHead>SOV Line</TableHead>
                              <TableHead>Qty</TableHead>
                              <TableHead>Unit</TableHead>
                              <TableHead className="text-right">Remaining</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {availableSovLines.map((line: any) => (
                              <TableRow key={line.id} className={cn(line.fullyAssigned && "opacity-50", selectedSovLineIds.has(line.id) && "bg-primary/5")}>
                                <TableCell>
                                  {line.fullyAssigned ? (
                                    <Badge variant="secondary" className="text-[10px]">Assigned</Badge>
                                  ) : (
                                    <Checkbox checked={selectedSovLineIds.has(line.id)} onCheckedChange={() => toggleLine(line.id)} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className="font-mono text-xs text-muted-foreground mr-1">#{line.line_number}</span>
                                  <span className="font-medium text-sm">{line.description}</span>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{Number(line.quantity).toLocaleString()}</TableCell>
                                <TableCell className="text-sm">{line.unit}</TableCell>
                                <TableCell className={cn("text-right text-sm font-medium", line.remaining <= 0 ? "text-destructive" : "")}>{fmt(line.remaining)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
                  <Button disabled={!selectedVendorId || !stepOneHasSelection} onClick={goToStep2}>
                    Next: Set Rates →
                  </Button>
                </div>
              </div>
            </>
          ) : (
            /* STEP 2 — Set Rates */
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="h-8 px-2"><ArrowLeft className="h-4 w-4" /></Button>
                  <div>
                    <DialogTitle>Set Rates</DialogTitle>
                    <DialogDescription>
                      {selectedVendor?.name} — {hasUnits ? `${selectedCells.size} unit-lines across ${gridSovLineIds.length} SOV line${gridSovLineIds.length !== 1 ? "s" : ""}` : `${selectedLines.length} line item${selectedLines.length > 1 ? "s" : ""}`}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Apply one rate to all:</span>
                  <Input type="number" step="0.01" min="0" className="w-28" placeholder="$/unit" value={bulkRate} onChange={e => setBulkRate(e.target.value)} />
                  <Button variant="outline" size="sm" disabled={!bulkRate && bulkRate !== "0"} onClick={() => applyRateToAll(bulkRate)}>Apply to All</Button>
                </div>

                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SOV Line</TableHead>
                        {hasUnits && <TableHead className="text-right">Units</TableHead>}
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead className="text-right">Sell Rate</TableHead>
                        <TableHead className="text-right">Est. Sub Rate</TableHead>
                        <TableHead className="text-right w-28">Rate</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rateLines.map((line: any) => {
                        const laborRate = Number(line.labor_unit_price ?? line.unit_price);
                        const subRate = Number(line.sub_unit_price ?? 0);
                        const myRate = Number(rates[line.id]) || 0;
                        const margin = getMargin(laborRate, myRate);
                        const exceedsSellRate = myRate > 0 && myRate >= laborRate;
                        const exceedsSubRate = subRate > 0 && myRate > subRate && !exceedsSellRate;
                        const isZeroRate = rates[line.id] !== "" && myRate === 0;

                        // Count cells and qty for this SOV line
                        let cellCount = 0;
                        let totalQty = 0;
                        if (hasUnits) {
                          selectedCells.forEach(key => {
                            const { sovLineId, unitId } = parseKey(key);
                            if (sovLineId === line.id) {
                              cellCount++;
                              const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
                              totalQty += si ? Number(si.quantity) : 0;
                            }
                          });
                        } else {
                          totalQty = Number(line.quantity);
                        }

                        return (
                          <React.Fragment key={line.id}>
                            <TableRow className={cn(
                              exceedsSellRate && "bg-destructive/5",
                              exceedsSubRate && "bg-yellow-50/50 dark:bg-yellow-950/30"
                            )}>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-sm">{line.description}</span>
                                  {isZeroRate && <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />}
                                </div>
                              </TableCell>
                              {hasUnits && <TableCell className="text-right font-mono text-sm">{cellCount}</TableCell>}
                              <TableCell className="text-right">
                                {hasUnits ? (
                                  <span className="font-mono text-sm">{totalQty.toLocaleString()}</span>
                                ) : (
                                  <div className="flex flex-col items-end gap-0.5">
                                    <Input
                                      type="number" step="0.01" min="0"
                                      max={totalQty}
                                      className="w-24 text-right h-7 text-sm"
                                      value={assignQty[line.id] ?? String(totalQty)}
                                      onChange={e => setAssignQty(prev => ({ ...prev, [line.id]: e.target.value }))}
                                    />
                                    <span className="text-[10px] text-muted-foreground">of {totalQty.toLocaleString()}</span>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-sm">{line.unit_of_measure ?? line.unit ?? ""}</TableCell>
                              <TableCell className="text-right text-sm">{fmtRate(laborRate)}</TableCell>
                              <TableCell className="text-right text-sm font-mono">{subRate > 0 ? fmtRate(subRate) : <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number" step="0.01" min="0"
                                  className={cn("w-24 text-right", exceedsSellRate && "border-destructive text-destructive", exceedsSubRate && "border-yellow-500")}
                                  value={rates[line.id] ?? ""}
                                  onChange={e => setRates(prev => ({ ...prev, [line.id]: e.target.value }))}
                                />
                              </TableCell>
                              <TableCell className="text-right text-sm font-medium">
                                {margin !== null ? (
                                  <span className={cn(margin >= 25 ? "text-green-600" : margin >= 20 ? "text-yellow-600" : "text-destructive")}>
                                    {pct(margin)} {margin >= 25 ? "🟢" : margin >= 20 ? "🟡" : "🔴"}
                                  </span>
                                ) : "—"}
                              </TableCell>
                            </TableRow>
                            {/* Sell rate violation */}
                            {exceedsSellRate && (
                              <TableRow>
                                <TableCell colSpan={hasUnits ? 8 : 7} className="py-1 px-4">
                                  <div className="flex items-center gap-2 text-sm text-destructive">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                    <span>Rate must be less than sell rate ({fmtRate(laborRate)}) — zero-margin pricing is not allowed</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            {/* Sub rate override — requires reason */}
                            {exceedsSubRate && (
                              <TableRow>
                                <TableCell colSpan={hasUnits ? 8 : 7} className="py-1 px-4">
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-400">
                                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                      <span>Rate exceeds estimated sub rate ({fmtRate(subRate)}) — variance reason required</span>
                                      {!can("approve_rate_overrides") && !isAdmin && (
                                        <Badge variant="destructive" className="text-[10px]">No override authority</Badge>
                                      )}
                                    </div>
                                    <Textarea
                                      placeholder="Explain why this rate exceeds the estimated sub cost…"
                                      className="h-16 text-sm"
                                      value={rateOverrideReasons[line.id] ?? ""}
                                      onChange={e => setRateOverrideReasons(prev => ({ ...prev, [line.id]: e.target.value }))}
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {zeroRateLines.length > 0 && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 text-sm">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <span><strong>{zeroRateLines.length} line{zeroRateLines.length > 1 ? "s" : ""}</strong> ha{zeroRateLines.length > 1 ? "ve" : "s"} $0 rate.</span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm bg-muted/50 rounded-md p-3">
                  <div className="space-x-4">
                    <span>WO Total: <span className="font-semibold">{fmt(woTotalAll)}</span></span>
                    <span>SOV Budget: <span className="font-semibold">{fmt(sovBudgetAll)}</span></span>
                  </div>
                  <span>
                    Margin:{" "}
                    <span className={cn("font-semibold", overallMargin >= 25 ? "text-green-600" : overallMargin >= 20 ? "text-yellow-600" : "text-destructive")}>
                      {pct(overallMargin)} {overallMargin >= 25 ? "🟢" : overallMargin >= 20 ? "🟡" : "🔴"}
                    </span>
                  </span>
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
                  <Button disabled={busy || !allRatesEntered} onClick={handleCreateWOs}>
                    {busy ? "Creating…" : `Create Work Order (${hasUnits ? selectedCells.size : selectedLines.length} lines)`}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {generateWO && project && (
        <GenerateWODialog
          open={!!generateWO}
          onOpenChange={(o) => !o && setGenerateWO(null)}
          workOrder={generateWO}
          woLines={woLines ?? []}
          project={project}
        />
      )}

      {/* Edit WO Dialog */}
      <Dialog open={!!editWO} onOpenChange={(o) => !o && setEditWO(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Work Order — {editWO?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subcontractor</label>
              <Select value={editWOVendor} onValueChange={setEditWOVendor}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contractor" />
                </SelectTrigger>
                <SelectContent>
                  {(vendors ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea value={editWODesc} onChange={(e) => setEditWODesc(e.target.value)} rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditWO(null)}>Cancel</Button>
              <Button onClick={handleSaveEditWO} disabled={editWOBusy}>{editWOBusy ? "Saving…" : "Save Changes"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Modal */}
      {voidWO && (
        <WOVoidModal
          open={!!voidWO}
          onOpenChange={(o) => !o && setVoidWO(null)}
          wo={voidWO}
          woLines={woLines ?? []}
          onComplete={() => { setVoidWO(null); refreshAll(); }}
        />
      )}

      {/* Close Modal */}
      {closeWO && (
        <WOCloseModal
          open={!!closeWO}
          onOpenChange={(o) => !o && setCloseWO(null)}
          wo={closeWO}
          woLines={woLines ?? []}
          onComplete={() => { setCloseWO(null); refreshAll(); }}
        />
      )}
      {/* Create Bill Dialog */}
      {billWO && (
        <WOCreateBillDialog
          open={!!billWO}
          onOpenChange={(o) => !o && setBillWO(null)}
          wo={billWO}
          woLines={woLines ?? []}
          projectId={projectId}
          onCreated={() => { setBillWO(null); refreshAll(); }}
        />
      )}
    </div>
  );
};
